import { ClausesNote } from '../components/engagement/ClausesNote';

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Loader2, PenLine, Type, MousePointerClick, Eraser, CheckCircle2, MessageSquare, Download } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  servicesEngagementAPI as API, LETTER_STATUS_META, BILLING_FREQUENCY_OPTIONS, fmtMoney, downloadLetterPdf,
} from '../lib/servicesEngagementAPI';

const billingLabel = (v) => (BILLING_FREQUENCY_OPTIONS.find((o) => o.value === v) || {}).label || v;

function SignaturePad({ onChange }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);

  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - r.left, y: t.clientY - r.top };
  };
  const start = (e) => { drawing.current = true; const c = canvasRef.current.getContext('2d'); const p = pos(e); c.beginPath(); c.moveTo(p.x, p.y); };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const c = canvasRef.current.getContext('2d'); const p = pos(e);
    c.lineWidth = 2; c.lineCap = 'round'; c.strokeStyle = '#0044CC'; c.lineTo(p.x, p.y); c.stroke();
  };
  const end = () => { if (drawing.current) { drawing.current = false; onChange(canvasRef.current.toDataURL('image/png')); } };
  const clear = () => { const c = canvasRef.current; c.getContext('2d').clearRect(0, 0, c.width, c.height); onChange(null); };

  return (
    <div className="space-y-2">
      <canvas
        ref={canvasRef} width={460} height={150}
        className="border rounded-lg bg-card w-full touch-none cursor-crosshair"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        data-testid="signature-canvas"
      />
      <Button size="sm" variant="ghost" onClick={clear}><Eraser className="w-4 h-4 mr-1" /> Clear</Button>
    </div>
  );
}

export default function ReviewSignEngagement() {
  const navigate = useNavigate();
  const [clientId, setClientId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [letter, setLetter] = useState(null);
  const [services, setServices] = useState([]);
  const [summary, setSummary] = useState({});
  const [paymentTerms, setPaymentTerms] = useState('');

  const [method, setMethod] = useState('type');
  const [drawData, setDrawData] = useState(null);
  const [typedName, setTypedName] = useState('');
  const [consent, setConsent] = useState(false);
  const [showChanges, setShowChanges] = useState(false);
  const [comment, setComment] = useState('');

  const load = async (cid) => {
    const [sel, ltr] = await Promise.all([API.listSelected(cid), API.getLetter(cid)]);
    setServices(sel.data.services || []);
    setSummary(sel.data.summary || {});
    setPaymentTerms(sel.data.payment_terms || '');
    setLetter(ltr.data.letter);
  };

  useEffect(() => {
    (async () => {
      try {
        const me = await api.get('/api/me');
        const cid = me.data.active_membership?.client_id;
        if (!cid) { toast.error('This account is not linked to an engagement'); navigate('/dashboard'); return; }
        setClientId(cid);
        await load(cid);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to load');
      } finally { setLoading(false); }
    })();
  }, [navigate]);

  const sign = async () => {
    const name = method === 'draw' ? typedName : typedName;
    if (!typedName.trim()) { toast.error('Please enter your full name'); return; }
    if (method === 'draw' && !drawData) { toast.error('Please draw your signature'); return; }
    if (!consent) { toast.error('Please tick the confirmation box'); return; }
    setBusy(true);
    try {
      const res = await API.sign(letter.id, {
        signature_method: method,
        signature_data: method === 'draw' ? drawData : typedName,
        signed_by_name: name,
        consent,
      });
      setLetter(res.data);
      toast.success('Engagement letter signed — thank you!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not sign');
    } finally { setBusy(false); }
  };

  const submitChanges = async () => {
    setBusy(true);
    try {
      const res = await API.requestChanges(letter.id, { comment });
      setLetter(res.data); setShowChanges(false);
      toast.success('Your request has been sent to your accountant');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="p-6"><Loader2 className="w-6 h-6 animate-spin text-foreground" /></div>;

  if (!letter) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card><CardContent className="py-12 text-center text-muted-foreground" data-testid="client-no-engagement-letter">
          No engagement letter yet. Your accountant will send one for you to review and sign.
        </CardContent></Card>
      </div>
    );
  }

  const signed = ['signed', 'stored'].includes(letter.status);

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Review Services &amp; Fees</h1>
        <p className="text-muted-foreground mt-1">Ref {letter.engagement_letter_number} · <Badge className={(LETTER_STATUS_META[letter.status] || {}).color}>{(LETTER_STATUS_META[letter.status] || {}).label || letter.status}</Badge></p>
      </div>

      {/* Services & fees (read-only) */}
      <Card>
        <CardHeader><CardTitle className="text-lg">Services &amp; Fees</CardTitle><CardDescription>These are the services and fees your accountant has proposed.</CardDescription></CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow className="bg-muted/50"><TableHead>Service</TableHead><TableHead>Billing</TableHead><TableHead>Fee</TableHead><TableHead>GST</TableHead><TableHead>Total</TableHead></TableRow></TableHeader>
            <TableBody>
              {services.map((s) => (
                <TableRow key={s.id}>
                  <TableCell>{s.service_name}{s.service_description ? <div className="text-xs text-muted-foreground/70">{s.service_description}</div> : null}</TableCell>
                  <TableCell className="text-sm">{billingLabel(s.billing_frequency)}</TableCell>
                  <TableCell className="text-sm">{fmtMoney(s.fee_amount)}</TableCell>
                  <TableCell className="text-sm">{fmtMoney(s.gst_amount)}</TableCell>
                  <TableCell className="text-sm font-medium">{fmtMoney(s.total_amount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="flex justify-end mt-3">
            <div className="w-64 space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{fmtMoney(summary.subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">GST</span><span>{fmtMoney(summary.gst)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Discount</span><span>-{fmtMoney(summary.discount)}</span></div>
              <div className="flex justify-between font-semibold text-foreground border-t pt-1"><span>Total payable</span><span>{fmtMoney(summary.total)}</span></div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-2">Payment terms: {paymentTerms}</p>
        </CardContent>
      </Card>

      {/* Letter content */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Engagement Letter</CardTitle>
          <Button size="sm" variant="outline" data-testid="client-download-letter-pdf"
            onClick={() => downloadLetterPdf(letter.id, letter.engagement_letter_number).catch(() => toast.error('Failed to download PDF'))}>
            <Download className="w-4 h-4 mr-1" /> Download PDF
          </Button>
        </CardHeader>
        <CardContent className="space-y-2">
          <ClausesNote state={letter.clauses_state} client />
          <div className="border rounded-lg p-4 bg-muted/50 max-h-[420px] overflow-y-auto" data-testid="letter-content-view">
            <pre className="whitespace-pre-wrap text-xs text-foreground/90 font-sans">{letter.letter_content}</pre>
          </div>
        </CardContent>
      </Card>

      {/* Sign / actions */}
      {signed ? (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="py-6 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-6 h-6 text-green-600" />
              <div>
                <p className="font-semibold text-green-800">Signed</p>
                <p className="text-sm text-green-700">Signed by {letter.signed_by_name} via {letter.signature_method}. A copy is stored in “My Engagement Letters”.</p>
              </div>
            </div>
            <Button size="sm" variant="outline" className="border-green-300 text-green-800" data-testid="signed-download-pdf"
              onClick={() => downloadLetterPdf(letter.id, letter.engagement_letter_number).catch(() => toast.error('Failed to download PDF'))}>
              <Download className="w-4 h-4 mr-1" /> Signed PDF
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader><CardTitle className="text-lg">Sign electronically</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              {[['type', Type, 'Type'], ['draw', PenLine, 'Draw'], ['click', MousePointerClick, 'Click to accept']].map(([m, Icon, label]) => (
                <Button key={m} variant={method === m ? 'default' : 'outline'} size="sm" onClick={() => setMethod(m)} data-testid={`sig-method-${m}`}>
                  <Icon className="w-4 h-4 mr-1" /> {label}
                </Button>
              ))}
            </div>

            <div className="space-y-2">
              <Label>Full name *</Label>
              <Input value={typedName} onChange={(e) => setTypedName(e.target.value)} data-testid="sig-name"
                className={method === 'type' ? 'text-lg' : ''} style={method === 'type' ? { fontFamily: 'cursive' } : {}} />
            </div>

            {method === 'draw' && <SignaturePad onChange={setDrawData} />}

            <label className="flex items-start gap-2 cursor-pointer bg-muted/50 border rounded-lg p-3">
              <Checkbox checked={consent} onCheckedChange={(v) => setConsent(!!v)} data-testid="sig-consent" className="mt-0.5" />
              <span className="text-sm text-muted-foreground">I confirm that I have read and accepted the engagement letter, including the scope of services, fees, privacy terms and client responsibilities.</span>
            </label>

            <div className="flex flex-wrap justify-between gap-2">
              <Button variant="outline" onClick={() => setShowChanges((s) => !s)}><MessageSquare className="w-4 h-4 mr-1" /> Request changes</Button>
              <Button onClick={sign} disabled={busy} data-testid="sign-submit">
                {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />} Sign &amp; accept
              </Button>
            </div>

            {showChanges && (
              <div className="space-y-2 border-t pt-3">
                <Label>What would you like changed?</Label>
                <Textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} />
                <div className="flex justify-end"><Button variant="outline" onClick={submitChanges} disabled={busy}>Send request</Button></div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
